// src/features/study/views/SurveyArtifactView.js
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSave, faEdit, faCopy, faDownload, faTrash } from "@fortawesome/free-solid-svg-icons";
import SurveyFormBuilder from "../../../components/SurveyFormBuilder";
import SurveyVariablesCard from "../../../components/artifacts/SurveyVariablesCard";
import "./SurveyArtifactView.scss";

export default function SurveyArtifactView({
  artifact,
  parseSurveyData,
  onSave,
  onDistribute,
  isEditing,
  isGenerating,
  onCancelEdit,
  onCopy,
  onDownloadWord,
  onDownloadPDF,
  onDownloadMarkdown,
  onDelete,
}) {
  const surveyData = parseSurveyData(artifact.content);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [isQuestionEditing, setIsQuestionEditing] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [selectedBlockIdForAdd, setSelectedBlockIdForAdd] = useState("");
  const surveyFormBuilderRef = React.useRef(null);

  // Fixed 툴바 관련 상태
  const [showFixedToolbar, setShowFixedToolbar] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(250);
  const toolbarRef = React.useRef(null);

  // 문항 타입 옵션
  const questionTypes = [
    { value: 'TextInput', label: '단답형', icon: '📝' },
    { value: 'TextArea', label: '서술형', icon: '📄' },
    { value: 'RadioButtons', label: '단일 선택', icon: '🔘' },
    { value: 'Checkboxes', label: '다중 선택', icon: '☑️' },
  ];

  // 사이드바 너비 계산 함수
  const updateSidebarWidth = React.useCallback(() => {
    const sidebar = document.querySelector(".common-sidebar") || document.querySelector(".b2b-sidebar");
    if (sidebar) {
      const isCollapsed = sidebar.classList.contains("collapsed");
      setSidebarWidth(isCollapsed ? 60 : 250);
    }
  }, []);

  // 스크롤 이벤트로 툴바가 화면에서 사라지는지 감지
  React.useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    const handleScroll = () => {
      const toolbarBottom = toolbar.getBoundingClientRect().bottom;
      const shouldShow = toolbarBottom < 0;
      setShowFixedToolbar(shouldShow);
      updateSidebarWidth();
    };

    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      document.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("scroll", handleScroll);
    };
  }, [updateSidebarWidth]);

  // 사이드바 접힘/펼침 상태 변화 감지
  React.useEffect(() => {
    const sidebar = document.querySelector(".common-sidebar") || document.querySelector(".b2b-sidebar");
    if (!sidebar) return;

    const observer = new MutationObserver(() => {
      updateSidebarWidth();
    });

    observer.observe(sidebar, { attributes: true, attributeFilter: ["class"] });
    updateSidebarWidth();

    return () => observer.disconnect();
  }, [updateSidebarWidth]);

  const handleAddQuestion = (elementType) => {
    if (surveyFormBuilderRef.current) {
      surveyFormBuilderRef.current.addQuestion(elementType, selectedBlockIdForAdd || null);
    }
    setShowAddQuestionModal(false);
    setSelectedBlockIdForAdd("");
  };

  const handleSaveQuestions = () => {
    if (surveyFormBuilderRef.current) {
      surveyFormBuilderRef.current.save();
    }
  };

  const handleDistributeQuestions = () => {
    if (surveyFormBuilderRef.current) {
      surveyFormBuilderRef.current.distribute();
    }
  };

  // 툴바 렌더링 함수 (원래 위치 & fixed 위치 공유)
  const renderToolbar = () => (
    <div className="survey-questions-section-toolbar">
      {/* 왼쪽 영역 */}
      <div className="survey-questions-toolbar-left">
        {!isQuestionEditing ? (
          <button
            className="survey-questions-toolbar-btn survey-questions-toolbar-btn--primary"
            onClick={() => setIsQuestionEditing(true)}
          >
            <FontAwesomeIcon icon={faEdit} />
            편집
          </button>
        ) : (
          <>
            <button
              className="survey-questions-toolbar-btn survey-questions-toolbar-btn--primary"
              onClick={() => {
                handleSaveQuestions();
                setIsQuestionEditing(false);
              }}
            >
              <FontAwesomeIcon icon={faSave} />
              편집 완료
            </button>
            <button
              className="survey-questions-toolbar-btn survey-questions-toolbar-btn--outline-primary"
              onClick={() => setShowAddQuestionModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              문항 추가
            </button>
            <button
              className="survey-questions-toolbar-btn survey-questions-toolbar-btn--secondary"
              onClick={() => setIsQuestionEditing(false)}
            >
              취소
            </button>
          </>
        )}
      </div>

      {/* 오른쪽 영역: 편집 모드가 아닐 때만 표시 */}
      {!isQuestionEditing && (
        <div className="survey-questions-toolbar-right">
          <button
            className="survey-questions-toolbar-btn survey-questions-toolbar-btn--secondary"
            onClick={onCopy}
          >
            <FontAwesomeIcon icon={faCopy} />
            복사
          </button>
          {/* 바로 PDF 다운로드 */}
          <button
            className="survey-questions-toolbar-btn survey-questions-toolbar-btn--secondary"
            onClick={onDownloadPDF}
          >
            <FontAwesomeIcon icon={faDownload} />
            다운로드
          </button>

          {/* 드롭다운 메뉴 - 임시 비활성화
          <div className="survey-questions-download-menu" style={{ position: 'relative' }}>
            <button
              className="survey-questions-toolbar-btn survey-questions-toolbar-btn--secondary"
              onClick={() => setDownloadOpen((v) => !v)}
            >
              <FontAwesomeIcon icon={faDownload} />
              다운로드
            </button>
            {downloadOpen && (
              <div className="survey-questions-download-dropdown">
                <button
                  className="survey-questions-download-item"
                  onClick={() => {
                    setDownloadOpen(false);
                    onDownloadPDF?.();
                  }}
                >
                  PDF
                </button>
              </div>
            )}
          </div>
          */}

          <button
            className="survey-questions-toolbar-btn survey-questions-toolbar-btn--danger"
            onClick={onDelete}
          >
            <FontAwesomeIcon icon={faTrash} />
            삭제
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* 변수 분석 결과 - 맨 위 */}
      <SurveyVariablesCard surveyData={surveyData} />
      
      {/* 문항 생성 결과 섹션 */}
      <div style={{ marginTop: "48px" }}>
        {/* 타이틀 */}
        <h2 className="survey-questions-section-title">문항 생성 결과</h2>
        
        {/* 원래 툴바 */}
        <div ref={toolbarRef} className="survey-questions-section-toolbar-wrapper">
          {renderToolbar()}
        </div>

        {/* Fixed 툴바: 스크롤로 원래 툴바가 사라지면 상단에 고정 */}
        {showFixedToolbar &&
          createPortal(
            <div 
              className="survey-fixed-toolbar-container"
              style={{ left: sidebarWidth }}
            >
              {renderToolbar()}
            </div>,
            document.body
          )}

        {showAddQuestionModal && (
          <div
            className="add-question-modal"
          >
            <div className="add-question-modal-content">
              <div className="modal-header">
                <h4>문항 타입 선택</h4>
                <button
                  className="modal-close-btn"
                  onClick={() => setShowAddQuestionModal(false)}
                >
                  ×
                </button>
              </div>
              <div style={{ marginBottom: "14px" }}>
                <div style={{ fontWeight: 700, marginBottom: "8px" }}>추가할 블록</div>
                <select
                  value={selectedBlockIdForAdd}
                  onChange={(e) => setSelectedBlockIdForAdd(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    fontWeight: 700,
                  }}
                >
                  <option value="">(기본값: 마지막 블록)</option>
                  {(surveyData?.blocks || []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.title || b.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="question-type-grid">
                {questionTypes.map((type) => (
                  <button
                    key={type.value}
                    className="question-type-btn"
                    onClick={() => handleAddQuestion(type.value)}
                  >
                    <span className="type-icon">{type.icon}</span>
                    <span className="type-label">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: "24px" }}>
          <SurveyFormBuilder
            ref={surveyFormBuilderRef}
            surveyData={surveyData}
            onSave={onSave}
            onDistribute={onDistribute}
            hideHeader={true}
            isEditing={isQuestionEditing}
          />
        </div>
      </div>
    </>
  );
}
